/**
 * Global Error Handlers
 * 
 * Mirrors the old project's UnhandledErrorHandler & SessionExpirationErrorHandler:
 * 
 * 1. UnhandledErrorHandler
 *    – Listens to `window.onerror` (runtime JS errors)
 *    – Listens to `window.onunhandledrejection` (unhandled Promise rejections)
 *    – Prevents default behaviour, logs everything via Logger service
 * 
 * 2. SessionExpirationHandler
 *    – Intercepts "Unauthorized" errors surfaced by the unhandled-rejection handler
 *    – Fires a custom event so the UI layer can show a toast / lock the screen
 * 
 * Usage:
 *    Call `initGlobalErrorHandlers()` once at application startup (e.g. index.tsx).
 */

import { logger } from '../services/logger';
import { UnauthorizedError } from '../errors';

// ─── Custom Event for Session Expiry ─────────────────────────────────
// UI components listen for this via `window.addEventListener`.
export const SESSION_EXPIRED_EVENT = 'session:expired';

export interface SessionExpiredDetail {
    message: string;
}

function dispatchSessionExpired(message: string) {
    window.dispatchEvent(
        new CustomEvent<SessionExpiredDetail>(SESSION_EXPIRED_EVENT, {
            detail: { message },
        })
    );
}

// ─── Unhandled Error Handler ─────────────────────────────────────────

function handleWindowError(event: ErrorEvent): void {
    event.preventDefault();

    try {
        logger.trackError(
            event.error instanceof Error ? event.error : new Error(event.message),
            'UnhandledErrorHandler.window.onerror'
        );
    } catch {
        // Fallback if logger itself fails
        console.error('[UnhandledErrorHandler] Logging failed. Original error:', event.message);
    }
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
    event.preventDefault();

    const reason = event.reason;

    try {
        // Detect session expiration
        if (reason instanceof UnauthorizedError) {
            logger.trackWarning(
                `Session expired: ${reason.message}`,
                'SessionExpirationHandler'
            );
            dispatchSessionExpired(reason.message || 'Session expired – please sign in again.');
            return;
        }

        // Generic unhandled rejection
        const error =
            reason instanceof Error
                ? reason
                : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');

        logger.trackError(error, 'UnhandledErrorHandler.unhandledrejection');
    } catch {
        console.error('[UnhandledErrorHandler] Logging failed. Original rejection:', reason);
    }
}

// ─── Bootstrap ───────────────────────────────────────────────────────

let initialized = false;

/**
 * Call once at app startup to install global error listeners.
 */
export function initGlobalErrorHandlers(): void {
    if (initialized) return;
    initialized = true;

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    logger.trackTrace('Global error handlers initialized.', 'initGlobalErrorHandlers');
}

/**
 * Teardown (useful for tests).
 */
export function removeGlobalErrorHandlers(): void {
    window.removeEventListener('error', handleWindowError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    initialized = false;
}
